import React, {FC, ReactNode} from 'react'
import {useNavigation} from '@react-navigation/native'
import {StackNavigationProp} from '@react-navigation/stack'
import {useTranslation} from 'react-i18next'
import {View} from 'react-native'

import {AppEvents, BankAccountManagementEvents} from 'src/lib/Analytics/app_events'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {LinkedAccountOwnershipSummarySubset} from 'src/products/MCU/AccountManagementV2/BankAccountInfo'
import {MainStackParamList} from 'src/nav/MainStackParamsList'
import {openContactUsForm} from 'src/lib/contactUs'
import {SvgLink} from 'src/designSystem/components/atoms/SvgLink/SvgLink'
import Box from 'src/designSystem/components/atoms/Box/Box'
import OverlaySimple from 'src/designSystem/components/organisms/Overlay/variants/OverlaySimple'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import {useOwnershipStatus} from 'src/products/MCU/AccountManagementV2/Modals/OwnershipStatusInfoModal/useOwnershipStatus'

type OwnershipStatusInfoModalProps = {
  visible: boolean
  onOkay: () => void
  ownershipSummary?: LinkedAccountOwnershipSummarySubset
  onAddNewAccount?: () => void
  testID?: string
}

const OwnershipStatusInfoModal: FC<OwnershipStatusInfoModalProps> = (props) => {
  const {
    visible: isVisible,
    onOkay: handleOnOkay,
    ownershipSummary,
    onAddNewAccount,
    testID,
  } = props
  const navigation: StackNavigationProp<MainStackParamList> = useNavigation()
  const {t} = useTranslation(['AccountOwnership', 'Common'])
  const contactUsButton = t('ContactUs')
  const contactUsLink = t('Common:ContactUsLowercase')

  const {
    doesAccountNeedReview,
    hasNameValidationError,
    hasStateValidationError,
    isBusinessAccount,
    isJointAccount,
  } = useOwnershipStatus({ownershipSummary})

  const title = (): string => {
    if (doesAccountNeedReview) {
      return t('YourAccountIsUnderReview')
    } else if (hasStateValidationError || hasNameValidationError) {
      return t('WeNeedToVerifySomeInformation')
    } else if (isBusinessAccount) {
      return t('PleaseLinkANewAccount')
    } else if (isJointAccount) {
      return t('NeedToVerifyJointAccount')
    } else {
      return t('YourAccountIsUnderReview')
    }
  }

  const onContactUs = (navigation: StackNavigationProp<MainStackParamList>): void => {
    TrackAppEvent(
      BankAccountManagementEvents.bank_ownership_contact_us_selected,
      AppEvents.Category.BankAccountManagement,
    )
    handleOnOkay()
    openContactUsForm(navigation)
  }

  const getModalContentForError = (): ReactNode => {
    if (hasNameValidationError && doesAccountNeedReview) {
      return null
    } else if (hasStateValidationError && !hasNameValidationError) {
      return (
        <Box marginBottom={'small'}>
          <PFText variant={'p'}>{t('NeedToVerifyAddress')}</PFText>
        </Box>
      )
    } else if (hasNameValidationError && !hasStateValidationError) {
      return (
        <>
          <Box marginBottom={'small'}>
            <PFText variant={'p'}>{t('NeedToVerifyNameOrLinkNewAccount')}</PFText>
          </Box>
          <Box>
            <PFText variant={'p'}>
              {t('FeelFreeTo')}
              <SvgLink
                onPress={(): void => onContactUs(navigation)}
                linkText={contactUsLink}
                linkType={'inline'}
              />
              {t('ForMoreAssistance')}
            </PFText>
          </Box>
        </>
      )
    } else if (hasNameValidationError && hasStateValidationError) {
      return (
        <Box marginBottom={'small'}>
          <PFText variant={'p'}>{t('NeedToVerifyNameAndAddress')}</PFText>
        </Box>
      )
    } else if (isBusinessAccount) {
      return (
        <>
          <Box marginBottom={'small'}>
            <PFText variant={'p'}>{t('PossibleOnlySupportsPersonallAccounts')}</PFText>
          </Box>
          <Box>
            <PFText variant={'p'}>
              {t('FeelFreeTo')}
              <SvgLink
                onPress={(): void => onContactUs(navigation)}
                linkText={contactUsLink}
                linkType={'inline'}
              />
              {t('ForMoreAssistance')}
            </PFText>
          </Box>
        </>
      )
    } else if (isJointAccount) {
      return (
        <Box marginBottom={'small'}>
          <PFText variant={'p'}>{t('PossibleSupportsSharedBankAccountsButMustVerify')}</PFText>
        </Box>
      )
    }
  }

  const getOkButtonText = (): string => {
    if (hasNameValidationError && doesAccountNeedReview) {
      return contactUsButton
    } else if (isBusinessAccount || (hasNameValidationError && !hasStateValidationError)) {
      return t('LinkANewAccount')
    } else if (hasStateValidationError && !hasNameValidationError) {
      return contactUsButton
    } else if (hasNameValidationError && hasStateValidationError) {
      return contactUsButton
    } else {
      return contactUsButton
    }
  }

  const handleGetOkButtonAction = (): void => {
    if (
      isBusinessAccount ||
      (hasNameValidationError && !hasStateValidationError && !doesAccountNeedReview)
    ) {
      if (onAddNewAccount) {
        onAddNewAccount()
        handleOnOkay()
      }
    } else {
      return onContactUs(navigation)
    }
  }

  return (
    <OverlaySimple
      visible={isVisible}
      title={title()}
      titleSize="h2"
      titleAlign="left"
      okayButtonText={getOkButtonText()}
      okayButtonSize="large"
      hideOnOkay={true}
      onOkay={handleGetOkButtonAction}
      padding="medium"
      testID={testID ?? 'Ownership-Status-Info-Modal'}
      onDismiss={handleOnOkay}
      dismissButtonText={t('Common:OkThanks')}
    >
      <View>{getModalContentForError()}</View>
    </OverlaySimple>
  )
}

export {OwnershipStatusInfoModal}
